import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { MapPin, Clock, ArrowRight, Briefcase, Heart, Rocket, Globe } from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import PageHero, { SectionHeader, FeatureGrid } from "../components/shared/PageHero";

const ROLES = [
  { title: "Senior ASO Strategist", team: "ASO", location: "Jaipur · Hybrid", type: "Full-time" },
  { title: "Senior iOS Engineer (Swift)", team: "Engineering", location: "Remote · India", type: "Full-time" },
  { title: "Flutter Engineer", team: "Engineering", location: "Jaipur · On-site", type: "Full-time" },
  { title: "UA Manager — Apple Search Ads", team: "Growth", location: "Remote · Global", type: "Full-time" },
  { title: "Motion Designer (Ad Creative)", team: "Creative", location: "Remote · India", type: "Full-time" },
  { title: "Retention Engineer — Push/Email", team: "Growth", location: "Remote · India", type: "Full-time" },
  { title: "SDR — Apps Outbound", team: "Sales", location: "Jaipur · On-site", type: "Full-time" },
  { title: "Content Lead — ASO & Growth", team: "Marketing", location: "Remote · India", type: "Full-time" },
];

const PERKS = [
  { icon: Heart, title: "Health & wellness", text: "Top-tier insurance for you and family, plus a no-questions mental-health stipend." },
  { icon: Rocket, title: "Real equity", text: "Every full-time teammate owns a slice of Lumotis. Not tokens — equity." },
  { icon: Globe, title: "Remote-first", text: "Most roles are remote across India. Optional Jaipur HQ for those who want it." },
  { icon: Briefcase, title: "Learning budget", text: "₹50,000/year for books, courses, conferences — zero approval drama." },
  { icon: Clock, title: "Flexible hours", text: "We care about output, not seat time. Own your calendar." },
  { icon: Heart, title: "4-day sprints", text: "Friday afternoons are for deep work, side projects, or rest. Your call." },
];

export default function CareersPage() {
  return (
    <>
      <Helmet>
        <title>Careers at Lumotis — Join India's #1 App Growth Agency</title>
        <meta
          name="description"
          content="Open roles at Lumotis. ASO strategists, iOS/Android engineers, UA managers and more. Remote-first across India with Jaipur HQ."
        />
        <link rel="canonical" href="https://lumotis.com/careers" />
      </Helmet>
      <Navbar />
      <main data-testid="careers-page">
        <PageHero
          testid="careers-hero"
          eyebrow="Careers"
          breadcrumb={[{ label: "Home", href: "/" }, { label: "Careers" }]}
          title="Help us rank the world's apps."
          titleAccent="Join Lumotis."
          subtitle="A remote-first team of 40+ growth engineers, strategists and creatives. Shipping from Jaipur to Jakarta. Always hiring people who give a damn."
          primaryCta={{ to: "#openings", label: "See open roles", testid: "careers-cta-primary" }}
          secondaryCta={{ to: "/about", label: "About Lumotis", testid: "careers-cta-secondary" }}
        />

        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200">
          <div className="container">
            <SectionHeader
              eyebrow="01 — Perks"
              title="How we take care of the team."
              description="Remote-first, equity-owning, learning-obsessed."
            />
            <FeatureGrid items={PERKS} testidPrefix="careers-perk" />
          </div>
        </section>

        <section id="openings" className="py-20 md:py-28 bg-white">
          <div className="container">
            <SectionHeader
              eyebrow="02 — Open roles"
              title="Eight roles open right now."
            />
            <ul className="border-t border-zinc-200">
              {ROLES.map((r, idx) => (
                <li
                  key={r.title}
                  className="group border-b border-zinc-200"
                  data-testid={`role-${idx}`}
                >
                  <Link
                    to="/contact"
                    className="grid grid-cols-1 md:grid-cols-12 gap-4 py-6 items-center hover:bg-zinc-50 px-2 -mx-2 transition-colors"
                  >
                    <div className="md:col-span-5">
                      <p className="font-display text-xl font-bold text-charcoal group-hover:text-electric transition-colors">
                        {r.title}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-xs uppercase tracking-widest text-zinc-500">
                        {r.team}
                      </span>
                    </div>
                    <div className="md:col-span-3 text-sm text-zinc-600 inline-flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-electric" /> {r.location}
                    </div>
                    <div className="md:col-span-2 md:text-right">
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-charcoal">
                        {r.type} <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
